import React, { useEffect, useState, useContext } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Link } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton, Card, Grid, Chip } from '@mui/joy';
import {
  Box,
  Typography,
  Select,
  Option,
  Modal,
  ModalDialog,
  ModalClose,
  Button,
  FormControl,
  FormLabel,
  Input,
  Autocomplete,
  Divider,
  Avatar,
  Textarea
} from '@mui/joy';
import EnhancedButton from '../components/EnhancedButton';
import EnhancedInput from '../components/EnhancedInput';
import { supabase } from '../supabaseClient';
import { AuthContext } from '../context/AuthContext.jsx';
import PageLayout from '../components/PageLayout';
import StyledTable from '../components/StyledTable';
import SubmittalAnalytics from '../components/SubmittalAnalytics';
import './Switch.css';

const SubmittalTracker = () => {
  const { userMetadata, user } = useContext(AuthContext);
  const [submittals, setSubmittals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    project_id: '',
    owner_id: '',
    deadline: '',
    submittal_date: '',
    on_hold: false
  });
  const [showAll, setShowAll] = useState(false);
  const [sortKey, setSortKey] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  // PDF Download
  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(14);
    doc.text('Submittal Schedule', 14, 16);
    autoTable(doc, {
      startY: 20,
      head: [['Project', 'Submittal', 'Owner', 'Deadline', 'Submittal Date', 'Days Remaining', 'On Hold']],
      body: sortedSubmittals.map(s => [
        s.project_name || 'Unnamed Project',
        s.name,
        s.owner_name,
        s.deadline ? new Date(s.deadline + 'T00:00:00').toLocaleDateString() : '—',
        s.submittal_date ? new Date(s.submittal_date).toLocaleDateString() : '—',
        s.days_to_submittal ?? '—',
        s.on_hold ? 'Yes' : 'No'
      ]),
      didParseCell: function (data) {
        if (data.section === 'body') {
          const s = sortedSubmittals[data.row.index];
          const rowStyle = getRowStyleByDeadline(s.deadline, s.submittal_date);
          const fillColor = extractColor(rowStyle.backgroundColor);
          if (fillColor) {
            data.cell.styles.fillColor = fillColor;
          }
        }
      }
    });
    doc.save('submittal_schedule.pdf');
  };

  const fetchData = async () => {
    // Fetch submittals, joining only projects (without clients, no owner join)
    const { data: submittalData } = await supabase
      .from('submittal_schedule')
      .select(`
        *,
        projects (
          id,
          name,
          client_id
        )
      `)
      .order('deadline', { ascending: true });

    // Fetch all projects (with client_id)
    const { data: projectData } = await supabase
      .from('projects')
      .select('id, name, client_id');

    // Fetch all users
    const { data: userData } = await supabase
      .from('users')
      .select('id, display_name, email');

    // Build user map: id -> user object
    const userMap = (userData || []).reduce((acc, u) => {
      acc[u.id] = u;
      return acc;
    }, {});

    if (submittalData) {
      const enriched = submittalData.map(s => {
        const deadlineDate = new Date(s.deadline);
        const submittalDate = s.submittal_date ? new Date(s.submittal_date) : null;
        const daysToDeadline = deadlineDate
          ? Math.ceil((deadlineDate - new Date()) / (1000 * 60 * 60 * 24))
          : null;
        // Find client name from project
        const project = s.projects;
        const client_id = project?.client_id;
        const client_name = 'Unknown Client'; // client_name not used, so set default
        // Attach owner_name using userMap and owner_id
        const owner = s.owner_id ? userMap[s.owner_id] : null;
        const owner_name = owner ? (owner.display_name || owner.email) : 'Unassigned';
        return {
          ...s,
          project_name: project?.name || 'Unknown Project',
          client_name,
          days_to_submittal: daysToDeadline,
          owner_name,
          on_hold: s.on_hold || false
        };
      });
      setSubmittals(enriched);
    }

    if (projectData) {
      projectData.sort((a, b) => a.name.localeCompare(b.name));
      setProjects(projectData);
    }
    if (userData) setUsers(userData);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = (s) => {
    setFormData({
      id: s.id,
      name: s.name,
      project_id: s.project_id,
      owner_id: s.owner_id,
      deadline: s.deadline,
      submittal_date: s.submittal_date || '',
      on_hold: s.on_hold || false
    });
    fetchComments(s.id);
    setOpen(true);
  };

  const fetchComments = async (submittalId) => {
    const { data, error } = await supabase
      .from('submittal_comments')
      .select(`
        *,
        users(display_name, email)
      `)
      .eq('submittal_id', submittalId)
      .order('created_at', { ascending: true });

    if (!error && data) {
      setComments(data);
    } else if (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !formData.id || !user?.id) return;

    setAddingComment(true);
    try {
      const { error } = await supabase
        .from('submittal_comments')
        .insert([{
          submittal_id: formData.id,
          user_id: user.id,
          comment_text: newComment
        }]);

      if (error) throw error;

      setNewComment('');
      await fetchComments(formData.id);
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Error adding comment: ' + error.message);
    } finally {
      setAddingComment(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown time';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleDelete = async (s) => {
    if (window.confirm(`Delete submittal "${s.name}"?`)) {
      await supabase.from('submittal_schedule').delete().eq('id', s.id);
      fetchData();
    }
  };

  const handleSubmit = async () => {
    const payload = {
      name: formData.name,
      project_id: formData.project_id,
      owner_id: formData.owner_id,
      deadline: formData.deadline,
      submittal_date: formData.submittal_date || null,
      on_hold: formData.on_hold
    };

    if (formData.id) {
      await supabase.from('submittal_schedule').update(payload).eq('id', formData.id);
    } else {
      await supabase.from('submittal_schedule').insert([payload]);
    }

    setOpen(false);
    setFormData({
      name: '',
      project_id: '',
      owner_id: '',
      deadline: '',
      submittal_date: '',
      on_hold: false
    });
    setComments([]);
    setNewComment('');
    fetchData();
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High':
        return 'red';
      case 'Medium':
        return 'orange';
      case 'Low':
        return 'gray';
      default:
        return 'transparent';
    }
  };

  const filteredSubmittals = showAll
    ? submittals
    : submittals.filter(s => {
        const today = new Date();
        const deadlineDate = s.deadline ? new Date(s.deadline) : null;
        const submittalDate = s.submittal_date ? new Date(s.submittal_date) : null;

        // Include submittals with no submittal_date
        if (!s.submittal_date) return true;

        return (
          (deadlineDate && deadlineDate >= today) ||
          (submittalDate && submittalDate >= today)
        );
      });

  const filteredAndOwnerSubmittals = ownerFilter
    ? filteredSubmittals.filter(s => s.owner_id === ownerFilter)
    : filteredSubmittals;

  const onSortChange = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  let sortedSubmittals = [...filteredAndOwnerSubmittals];
  if (sortKey) {
    sortedSubmittals.sort((a, b) => {
      let aValue = a[sortKey];
      let bValue = b[sortKey];
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }

  return (
    <PageLayout title="Submittal Schedule Tracker">
      <SubmittalAnalytics submittals={submittals} />
      <Card 
        sx={{ 
          p: 3, 
          mb: 3,
          background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.02) 0%, rgba(0, 126, 51, 0.02) 100%)',
          border: '1px solid',
          borderColor: 'success.200',
        }}
        className="fade-in"
      >
        <Typography 
          level="h4" 
          sx={{ 
            mb: 2, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
            tune
          </span>
          Controls & Filters
        </Typography>
        
        <Grid container spacing={2} alignItems="flex-end">
          <Grid xs={12} sm="auto">
            <EnhancedButton 
              onClick={() => setOpen(true)} 
              gradient
              startDecorator={
                <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                  add_circle
                </span>
              }
            >
              Create New Submittal
            </EnhancedButton>
          </Grid>
          
          <Grid xs={12} sm="auto">
            <EnhancedButton 
              variant="outlined" 
              onClick={() => setShowAll(!showAll)}
              startDecorator={
                <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                  {showAll ? 'visibility_off' : 'visibility'}
                </span>
              }
            >
              {showAll ? 'Show Upcoming' : 'Show All'}
            </EnhancedButton>
          </Grid>
          
          <Grid xs={12} sm={4} md={3}>
            <FormControl sx={{ width: '100%' }}>
              <FormLabel>Filter by Owner</FormLabel>
              <Select
                value={ownerFilter}
                onChange={(e, val) => setOwnerFilter(val || '')}
                placeholder="All Owners"
                size="sm"
                sx={{
                  '&:focus-within': {
                    borderColor: 'success.500',
                    boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                  },
                }}
              >
                <Option value="">All Owners</Option>
                {users.map((user) => (
                  <Option key={user.id} value={user.id}>
                    {user.display_name || user.email}
                  </Option>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid xs={12} sm="auto">
            <EnhancedButton 
              variant="outlined"
              onClick={handleDownloadPDF}
              startDecorator={
                <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                  download
                </span>
              }
            >
              Download PDF
            </EnhancedButton>
          </Grid>
        </Grid>
      </Card>
      <Card 
        sx={{ 
          p: 2, 
          mb: 3,
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          border: '1px solid',
          borderColor: 'neutral.200',
        }}
        className="fade-in"
      >
        <Typography 
          level="h6" 
          sx={{ 
            mb: 2, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
            legend_toggle
          </span>
          Status Legend
        </Typography>
        
        <Grid container spacing={2}>
          <Grid xs={12} sm={6} md={3}>
            <Chip
              variant="solid" 
              sx={{ 
                bgcolor: '#ff9999',
                color: '#8B0000',
                border: '1px solid #cc0000',
                '&:hover': { bgcolor: '#ff8080' }
              }}
              startDecorator={
                <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                  error
                </span>
              }
            >
              Overdue
            </Chip>
          </Grid>
          
          <Grid xs={12} sm={6} md={3}>
            <Chip
              variant="solid"
              sx={{ 
                bgcolor: '#ffcccc',
                color: '#8B4513',
                '&:hover': { bgcolor: '#ffb3b3' }
              }}
              startDecorator={
                <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                  schedule
                </span>
              }
            >
              Due in 7 Days
            </Chip>
          </Grid>
          
          <Grid xs={12} sm={6} md={3}>
            <Chip
              variant="solid"
              sx={{ 
                bgcolor: '#ffe5b4',
                color: '#8B6914',
                '&:hover': { bgcolor: '#ffdc9a' }
              }}
              startDecorator={
                <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                  warning
                </span>
              }
            >
              Due in 8–14 Days
            </Chip>
          </Grid>
          
          <Grid xs={12} sm={6} md={3}>
            <Chip
              variant="solid"
              sx={{ 
                bgcolor: '#d4edda',
                color: '#28a745',
                border: '1px solid #28a745',
                '&:hover': { bgcolor: '#c3e6cb' }
              }}
              startDecorator={
                <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                  check_circle
                </span>
              }
            >
              Completed
            </Chip>
          </Grid>
        </Grid>
      </Card>

      <Modal open={open} onClose={() => {
        setOpen(false);
        setComments([]);
        setNewComment('');
      }}>
        <ModalDialog 
          sx={{ 
            maxWidth: '500px',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid',
            borderColor: 'success.200',
          }}
        >
          <ModalClose />
          <Typography 
            level="h4" 
            sx={{ 
              mb: 3,
              color: 'success.700',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '28px', fontFamily: 'Material Symbols Outlined' }}>
              {formData.id ? 'edit' : 'add_circle'}
            </span>
            {formData.id ? 'Edit Submittal' : 'Create Submittal'}
          </Typography>
          
          <Grid container spacing={2}>
            <Grid xs={12}>
              <EnhancedInput
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    title
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12} sm={6}>
              <EnhancedInput
                label="Deadline"
                type="date"
                value={formData.deadline}
                onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                required
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    event
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12} sm={6}>
              <EnhancedInput
                label="Submittal Date"
                type="date"
                value={formData.submittal_date}
                onChange={(e) => setFormData({ ...formData, submittal_date: e.target.value })}
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    check_circle
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12}>
              <FormControl>
                <FormLabel>Project</FormLabel>
                <Select
                  value={formData.project_id}
                  onChange={(e, val) => setFormData({ ...formData, project_id: val })}
                  sx={{
                    '&:focus-within': {
                      borderColor: 'success.500',
                      boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                    },
                  }}
                >
                  {projects.map((project) => (
                    <Option key={project.id} value={project.id}>{project.name}</Option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid xs={12}>
              <FormControl>
                <FormLabel>Owner</FormLabel>
                <Select
                  value={formData.owner_id}
                  onChange={(e, val) => setFormData({ ...formData, owner_id: val })}
                  sx={{
                    '&:focus-within': {
                      borderColor: 'success.500',
                      boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                    },
                  }}
                >
                  {users.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {user.display_name || user.email}
                    </Option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid xs={12}>
              <FormControl orientation="horizontal" sx={{ alignItems: 'center', gap: 2 }}>
                <Chip
                  variant={formData.on_hold ? "solid" : "outlined"}
                  color={formData.on_hold ? "warning" : "neutral"}
                  onClick={() => setFormData({ ...formData, on_hold: !formData.on_hold })}
                  sx={{ cursor: 'pointer' }}
                  startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                      {formData.on_hold ? 'pause_circle' : 'play_circle'}
                    </span>
                  }
                >
                  {formData.on_hold ? 'On Hold' : 'Active'}
                </Chip>
              </FormControl>
            </Grid>
            
            {/* Comments Section - Only show for existing submittals */}
            {formData.id && (
              <Grid xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography level="title-sm" sx={{ mb: 2 }}>
                  Comments ({comments.length})
                </Typography>
                
                {/* Existing Comments */}
                <Box sx={{ maxHeight: 200, overflowY: 'auto', mb: 2 }}>
                  {comments.length > 0 ? (
                    comments.map((comment) => (
                      <Box key={comment.id} sx={{ display: 'flex', gap: 1, mb: 2, p: 2, bgcolor: 'neutral.50', borderRadius: 'sm' }}>
                        <Avatar size="sm" sx={{ bgcolor: 'success.500', color: 'white' }}>
                          {getInitials(comment.users?.display_name)}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography level="title-xs">
                              {comment.users?.display_name || 'Unknown User'}
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                              {formatDate(comment.created_at)}
                            </Typography>
                          </Box>
                          <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                            {comment.comment_text}
                          </Typography>
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 2 }}>
                      No comments yet.
                    </Typography>
                  )}
                </Box>

                {/* Add Comment */}
                <Box>
                  <Typography level="title-xs" sx={{ mb: 1 }}>
                    Add a comment
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Textarea
                      placeholder="Write a comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleAddComment();
                        }
                      }}
                      minRows={2}
                      sx={{ flexGrow: 1 }}
                    />
                    <EnhancedButton
                      onClick={handleAddComment}
                      loading={addingComment}
                      disabled={!newComment.trim()}
                      size="sm"
                      variant="solid"
                    >
                      Post
                    </EnhancedButton>
                  </Box>
                </Box>
              </Grid>
            )}
            
            <Grid xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                <EnhancedButton
                  variant="outlined"
                  onClick={() => {
                    setOpen(false);
                    setComments([]);
                    setNewComment('');
                  }}
                >
                  Cancel
                </EnhancedButton>
                <EnhancedButton
                  gradient
                  onClick={handleSubmit}
                  startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                      save
                    </span>
                  }
                >
                  {formData.id ? 'Update' : 'Create'}
                </EnhancedButton>
              </Box>
            </Grid>
          </Grid>
        </ModalDialog>
      </Modal>


      {sortedSubmittals.length > 0 ? (
        <Box sx={{ mt: 3 }}>
          <StyledTable
            columns={[
              { key: 'project_name', label: 'Project' },
              { key: 'name', label: 'Submittal' },
              { key: 'owner_name', label: 'Owner' },
              { key: 'deadline', label: 'Deadline' },
              { key: 'submittal_date', label: 'Submittal Date' },
              { key: 'days_to_submittal', label: 'Days Remaining' },
              { key: 'on_hold_toggle', label: 'On Hold' },
              { key: 'actions', label: 'Actions' }
            ]}
            rows={sortedSubmittals.map((s) => ({
              project_name: (
                <Link to={`/projects/${s.project_id}`} style={{ textDecoration: 'underline' }}>
                  {s.project_name || 'Unnamed Project'}
                </Link>
              ),
              name: s.name,
              owner_name: s.owner_name,
              deadline: s.deadline ? new Date(s.deadline + 'T00:00:00').toLocaleDateString() : '—',
              submittal_date: s.submittal_date ? new Date(s.submittal_date).toLocaleDateString() : '—',
              days_to_submittal: (() => {
                if (typeof s.days_to_submittal !== 'number') return '—';
                if (s.days_to_submittal === 0) return 'Due Today';
                if (s.days_to_submittal < 0) return `Overdue by ${Math.abs(s.days_to_submittal)} day${Math.abs(s.days_to_submittal) !== 1 ? 's' : ''}`;
                return `Due in ${s.days_to_submittal} day${s.days_to_submittal !== 1 ? 's' : ''}`;
              })(),
              on_hold_toggle: (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={s.on_hold}
                      onChange={async (e) => {
                        await supabase
                          .from('submittal_schedule')
                          .update({ on_hold: e.target.checked })
                          .eq('id', s.id);
                        fetchData(); // Refresh after update
                      }}
                    />
                    <span className="slider round"></span>
                  </label>
                </Box>
              ),
              actions: (
                <>
                  <IconButton size="sm" color="neutral" onClick={() => handleEdit(s)}><EditIcon /></IconButton>
                  <IconButton size="sm" color="danger" onClick={() => handleDelete(s)}><DeleteIcon /></IconButton>
                </>
              ),
              _rowSx: s.on_hold
                ? { backgroundColor: 'transparent', fontStyle: 'italic', color: '#666' }
                : getRowStyleByDeadline(s.deadline, s.submittal_date)
            }))}
            onSortChange={onSortChange}
            sortState={{ sortKey, sortOrder }}
          />
        </Box>
      ) : (
        <Typography level="body2" sx={{ mt: 4, textAlign: 'center' }}>No submittals found.</Typography>
      )}
    </PageLayout>
  );
};

export default SubmittalTracker;
// Returns a style object for the row based on deadline proximity.
function getRowStyleByDeadline(deadline, submittal_date) {
  if (!deadline) return {};

  const today = new Date();
  const deadlineDate = new Date(deadline);
  const submittalDate = submittal_date ? new Date(submittal_date) : null;
  const submittalMissing = !submittal_date;
  const diffDays = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));

  // Submittal and deadline both in the past
  if (deadlineDate < today && submittalDate && submittalDate < today) {
    return {
      backgroundColor: '#d4edda',
      borderLeft: '5px solid #28a745',
      color: '#333'
    };
  }

  // Due today or overdue with no submittal
  if (diffDays <= 0 && submittalMissing) {
    return {
      backgroundColor: '#ff9999',
      borderLeft: '5px solid #cc0000',
      fontWeight: 'bold',
      color: '#333'
    };
  } else if (diffDays <= 7) {
    return { backgroundColor: '#ffcccc', color: '#333' };
  } else if (diffDays <= 14) {
    return { backgroundColor: '#ffe5b4', color: '#333' };
  } else {
    return {};
  }
}

// Helper to extract RGB color array from known hex values
function extractColor(hex) {
  if (!hex) return null;
  switch (hex.toLowerCase()) {
    case '#ff9999': return [255, 153, 153];
    case '#ffcccc': return [255, 204, 204];
    case '#ffe5b4': return [255, 229, 180];
    case '#d4edda': return [212, 237, 218];
    default: return null;
  }
}